import { useTranslation } from "react-i18next"

import type { LocalAiLeadAdoptionCurve } from "@/api/local-ai-lead-api"

import { TrendingUp } from "@/components/icons"
import { DashboardSectionCard } from "@/features/admin-dashboard/components/admin-dashboard-shared"

import { monotoneCurvePath } from "./lead-adoption-curve-utils"

type LeadAdoptionCurveProps = {
  curve: LocalAiLeadAdoptionCurve
  formatCurrency: (value: number) => string
}

export function LeadAdoptionCurve({ curve, formatCurrency }: LeadAdoptionCurveProps) {
  const { t } = useTranslation()
  const points = curve.points
  const today = points.find((point) => point.activeUsers === curve.todayUsers)
  const values = points.map((point) => point.annualCostPerUser)
  if (curve.benchmarkAnnualPerSeat != null) values.push(curve.benchmarkAnnualPerSeat)
  const maxCost = Math.max(...values, 1)
  const tickStep = 10 ** Math.floor(Math.log10(maxCost)) / 2
  const yMax = Math.ceil((maxCost * 1.08) / tickStep) * tickStep
  const minUsers = points.length ? Math.min(...points.map((point) => point.activeUsers)) : 0
  const maxUsers = Math.max(...points.map((point) => point.activeUsers), 1)

  const renderChart = (compact: boolean) => {
    const width = compact ? 300 : 680
    const height = compact ? 220 : 250
    const padLeft = compact ? 84 : 96
    const padRight = compact ? 12 : 24
    const padTop = 20
    const plotRight = width - padRight
    const plotBottom = height - (compact ? 44 : 48)
    const xOf = (users: number) =>
      maxUsers === minUsers
        ? (padLeft + plotRight) / 2
        : padLeft + ((users - minUsers) / (maxUsers - minUsers)) * (plotRight - padLeft)
    const yOf = (cost: number) => padTop + (1 - cost / yMax) * (plotBottom - padTop)
    const xTicks = [
      points[0],
      ...(compact ? [] : [points[Math.floor((points.length - 1) / 2)]]),
      points.at(-1),
    ]
      .filter((point) => point != null)
      .filter(
        (point, index, ticks) =>
          ticks.findIndex((tick) => tick.activeUsers === point.activeUsers) === index
      )
    const path = monotoneCurvePath(
      points.map((point) => ({ x: xOf(point.activeUsers), y: yOf(point.annualCostPerUser) }))
    )

    return (
      <svg
        aria-label={t("localAiLeadPage.curve.title", "Cost per user as adoption grows")}
        role="img"
        viewBox={`0 0 ${width} ${height}`}
        className={
          compact
            ? "block h-auto w-full sm:hidden"
            : "hidden h-auto w-full max-w-[760px] sm:mx-auto sm:block"
        }
      >
        {[yMax, yMax / 2, 0].map((cost) => (
          <g key={cost}>
            <line
              stroke="hsl(var(--border))"
              x1={padLeft}
              x2={plotRight}
              y1={yOf(cost)}
              y2={yOf(cost)}
            />
            <text
              fill="hsl(var(--muted-foreground))"
              fontSize={compact ? 10 : 11}
              textAnchor="end"
              x={padLeft - 10}
              y={yOf(cost) + 4}
            >
              {formatCurrency(cost)}
            </text>
          </g>
        ))}
        {xTicks.map((point) => (
          <text
            key={point.activeUsers}
            fill="hsl(var(--muted-foreground))"
            fontSize={compact ? 10 : 11}
            textAnchor="middle"
            x={xOf(point.activeUsers)}
            y={plotBottom + 20}
          >
            {point.activeUsers.toLocaleString()}
          </text>
        ))}
        {curve.benchmarkAnnualPerSeat != null ? (
          <line
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="5 5"
            x1={padLeft}
            x2={plotRight}
            y1={yOf(curve.benchmarkAnnualPerSeat)}
            y2={yOf(curve.benchmarkAnnualPerSeat)}
          />
        ) : null}
        <path d={path} fill="none" stroke="hsl(var(--foreground))" strokeWidth="2.5" />
        {today ? (
          <>
            <line
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="3 4"
              x1={xOf(today.activeUsers)}
              x2={xOf(today.activeUsers)}
              y1={yOf(today.annualCostPerUser)}
              y2={plotBottom}
            />
            <circle
              cx={xOf(today.activeUsers)}
              cy={yOf(today.annualCostPerUser)}
              fill="hsl(var(--primary))"
              r="6"
              stroke="hsl(var(--foreground))"
              strokeWidth="2"
            />
          </>
        ) : null}
        <text
          fill="hsl(var(--muted-foreground))"
          fontSize="12"
          textAnchor="middle"
          x={(padLeft + plotRight) / 2}
          y={height - 5}
        >
          {t("localAiLeadPage.curve.axis", "Active users")}
        </text>
      </svg>
    )
  }

  return (
    <DashboardSectionCard
      icon={TrendingUp}
      id="local-ai-lead-adoption-curve"
      title={t("localAiLeadPage.curve.title", "Cost per user as adoption grows")}
      description={t(
        "localAiLeadPage.curve.description",
        "The shared platform cost is split across everyone. Each new active user lowers the cost per user. Figures are per year."
      )}
    >
      {points.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("localAiLeadPage.curve.empty", "Not enough activity to draw the curve yet.")}
        </p>
      ) : (
        <>
          {renderChart(true)}
          {renderChart(false)}
          <table className="sr-only">
            <caption>{t("localAiLeadPage.curve.title", "Cost per user as adoption grows")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("localAiLeadPage.curve.axis", "Active users")}</th>
                <th scope="col">
                  {t("localAiLeadPage.curve.annualCostAxis", "Cost per user per year")}
                </th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.activeUsers}>
                  <td>{point.activeUsers.toLocaleString()}</td>
                  <td>{formatCurrency(point.annualCostPerUser)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-3 text-sm">
        {curve.todayAnnualCostPerUser != null ? (
          <span className="flex items-center gap-2 font-medium">
            <span className="border-foreground bg-primary h-2.5 w-2.5 shrink-0 rounded-full border" />
            {t("localAiLeadPage.curve.today", "Today · {{users}} users · {{cost}} / user / year", {
              users: curve.todayUsers,
              cost: formatCurrency(curve.todayAnnualCostPerUser),
            })}
          </span>
        ) : null}
        {curve.benchmarkAnnualPerSeat != null ? (
          <span className="text-muted-foreground flex items-center gap-2">
            <span className="border-muted-foreground w-4 shrink-0 border-t border-dashed" />
            {t("localAiLeadPage.curve.benchmark", "Seat benchmark {{cost}} / year", {
              cost: formatCurrency(curve.benchmarkAnnualPerSeat),
            })}
          </span>
        ) : null}
      </div>
    </DashboardSectionCard>
  )
}
